// One row in the queue's left column. Extracted from PurchasingQueuePage.js at
// Session 79.14c.
//
// #415 LANDS HERE: the vendor's order number gets a control on this card, beside
// the tracking one, on `ordered` rows only and at secondary weight. #545 lands
// here too — this card shows the request number and the work order and has never
// shown the PO number Stede issued.

import React, { useState, useEffect } from 'react';
import { IconTruck } from '@tabler/icons-react';
import { fmtDate } from '../../lib/utils';
import { BRANCHES } from '../../lib/purchasingQueue';
import { itemUom, trackingSummary, trackingLabel, preferredVendorPicks } from '../../lib/requestItems';
import AgeBadge from './AgeBadge';
import ConfirmationBadge from './ConfirmationBadge';
import OrderIdentifiers from '../OrderIdentifiers';

// The card's headline already IS the request number, top-left, paired with the
// age badge — so the block below leaves it out rather than printing the same
// number twice on one card.
//
// The card ALSO passes `omitEmpty`, which is Colby's ruling at 79.14d (#546):
// a card shows the numbers actually tied to the order at the moment it renders,
// and nothing for the ones that do not exist. D15's labelled dash is kept where
// it earns its place — the detail panel, where somebody is studying ONE record
// and a vanished field is ambiguous. On a list you are comparing rows.
const CARD_IDENTIFIERS = ['po_ref', 'vendor_order_ref', 'work_order_ref', 'invoice_ref', 'tracking_number'];

export default function RequestCard({ req, isMobile, selectedReq, setSelectedReq, setMobileDetailReq, openMarkOrdered, openTracking, openApproval, canAction, canApprovePurchase, onSaveVendorOrderRef, leadKey = null }) {
  // #415, Session 79.14f. Colby: "vendor order number needs to add from the order
  // card in the left column like tracking can."
  //
  // INLINE ON THE CARD rather than a second full-width button, which is the
  // recommendation #415 carries and the reason it was not built as drawn at
  // 78n8.3. A second equal-weight button would imply tracking and the vendor's
  // reference matter equally, and they do not.
  //
  // NOT the `Unconfirmed` chip made tappable, which was the older suggestion and
  // Colby overruled it: that chip measures roughly 14px, under a third of the
  // 44px touch minimum #513 exists to enforce. The chip stays a state indicator.
  const [voEditing, setVoEditing] = useState(false);
  const [voValue, setVoValue] = useState(req.vendor_order_ref || '');
  const [voSaving, setVoSaving] = useState(false);
  const [voError, setVoError] = useState('');

  // Cards are reused as the list re-sorts and reloads, so the draft has to
  // follow the record rather than persist across it -- the same reason
  // VendorOrderNumberRow does this, and the same defect if it does not.
  useEffect(() => {
    setVoValue(req.vendor_order_ref || '');
    setVoEditing(false);
    setVoError('');
  }, [req.id, req.vendor_order_ref]);

  const items = req.material_request_items || [];
  const item = items[0];
  const itemName = item?.is_off_catalog
    ? (item.off_catalog_description || req.request_number)
    : (item?.inventory_items?.name || '—');
  const extraCount = Math.max(0, items.length - 1);
  const track = trackingSummary(items);
  const qty = item?.qty_requested ?? '—';
  const uom = itemUom(item);
  const branchName = BRANCHES.find(b => b.id === req.branch_id)?.name || '—';
  const isSelected = selectedReq?.id === req.id;
  const isPending = req.status === 'approved';
  const isNeedsAction = isPending;
  const isOrdered = req.status === 'ordered';
  const isPendingApproval = req.status === 'pending_purchase_approval';
  // #256. Approval used to write status 'ordered', which recorded an order nobody had
  // placed - Stede transmits nothing to a vendor, a human still has to. Approval now
  // returns the request here, to Needs Action, carrying the marker below. Denial also
  // lands on status 'approved', so status alone cannot tell the two apart; approved_at
  // is what separates them, because submitDenyPurchase() nulls it and only the spend
  // approval ever writes it.
  const isApprovedToOrder = req.status === 'approved' && req.approved_at != null;
  const approvedAmount = req.approved_amount != null ? parseFloat(req.approved_amount) : null;
  // #231. The card announced a stage and disclosed nothing about it — not the amount that
  // tripped the gate, not the threshold, not who can clear it. Colby read this stage as the
  // branch-manager approval gate during 74k5 and concluded the flow was backwards; if the
  // person who designed it misread it, a purchasing employee at a branch will. Both values
  // are already frozen on the row by submitMarkOrdered(), so this is disclosure, not new data.
  const approvalTotal = req.ordered_price != null ? parseFloat(req.ordered_price) : null;
  const approvalThreshold = req.approval_threshold_amount != null ? parseFloat(req.approval_threshold_amount) : null;

  const vendorPicks = preferredVendorPicks(items);
  const preferred = items.length === 1 ? item?.inventory_items?.vendor_items?.find(vi => vi.is_preferred) : null;
  const preferredVendor = preferred?.inventory_vendors;
  const vendorSnippet = items.length > 1
    ? (vendorPicks.length > 0
        ? `${vendorPicks.length} preferred vendor${vendorPicks.length !== 1 ? 's' : ''} across ${items.length} items`
        : null)
    : (preferredVendor
        ? `${preferredVendor.name}${preferred.unit_cost != null ? ' · $' + parseFloat(preferred.unit_cost).toFixed(2) + '/' + (uom || 'unit') : ''}`
        : null);

  const eta = track.earliestEta
    ? fmtDate(track.earliestEta)
    : (req.tracking_eta ? fmtDate(req.tracking_eta) : null);

  async function handleVoSave() {
    setVoSaving(true);
    setVoError('');
    const err = await onSaveVendorOrderRef(req, voValue);
    setVoSaving(false);
    if (err) { setVoError(err); return; }
    setVoEditing(false);
  }

  return (
    <div
      onClick={() => isMobile ? setMobileDetailReq(req) : setSelectedReq(isSelected ? null : req)}
      style={{
        background: isSelected ? '#F0FFF8' : 'white',
        border: `1px solid ${isSelected ? '#5DCAA5' : isPendingApproval ? '#D4537E' : isNeedsAction ? '#EF9F27' : '#E8E8E8'}`,
        borderRadius: 10,
        padding: '10px 12px',
        cursor: isMobile ? 'default' : 'pointer',
        marginBottom: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
        <span style={{ fontSize: 10, color: '#9A9A9A', letterSpacing: 0.3, flexShrink: 0 }}>{req.request_number}</span>
        <AgeBadge ts={req.created_at} />
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 3 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {itemName}
              {extraCount > 0 && <span style={{ fontWeight: 400, color: '#5C5C5C' }}> +{extraCount} more</span>}
            </div>
            {item?.is_off_catalog && (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#A32D2D', background: '#FFEBEE', border: '1px solid #FFCDD2', borderRadius: 4, padding: '1px 6px', flexShrink: 0 }}>
                Not in catalog
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 8 }}>
          {/* #410. Sits beside the ETA because they answer the same question from
              opposite ends: the ETA says when the vendor claims it arrives, this
              says whether the vendor has said anything at all. */}
          <ConfirmationBadge req={req} />
          {eta && <span style={{ fontSize: 10, background: '#E6F1FB', color: '#0C447C', borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>{track.tracked > 1 ? 'Earliest ETA' : 'ETA'} {eta}</span>}
        </div>
      </div>

      <div style={{ fontSize: 11, color: '#7A7A7A', marginBottom: isMobile ? 3 : 0 }}>
        {branchName} · {req.users?.full_name || '—'} · {items.length > 1 ? `${items.length} items` : `${qty} ${uom}`}
      </div>

      {/* #545. The work order used to sit in the line above as "· WO# 794001",
          and it was the only identifier on this card besides the request number.
          The PO number Stede itself issued appeared nowhere — Jennifer reads it
          down the phone to the vendor and then cannot find it on the screen she
          placed the order from. It is here now, and so is everything else the
          order actually carries. */}
      <OrderIdentifiers req={req} only={CARD_IDENTIFIERS} leadKey={leadKey} omitEmpty style={{ marginTop: 5 }} />

      {isPendingApproval && (
        <div style={{ background: '#FBEAF0', border: '1px solid #ED93B1', borderRadius: 6, padding: '6px 8px', marginTop: 6 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#72243E' }}>
              {approvalTotal != null ? `$${approvalTotal.toFixed(2)}` : 'No order total on file'}
            </span>
            {approvalThreshold != null && (
              <span style={{ fontSize: 10, color: '#993556', flexShrink: 0 }}>over ${approvalThreshold.toFixed(2)} limit</span>
            )}
          </div>
          <div style={{ fontSize: 10, color: '#993556', marginTop: 2 }}>Admin or executive can approve</div>
        </div>
      )}

      {isApprovedToOrder && (
        <div style={{ background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 6, padding: '6px 8px', marginTop: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1B5E20' }}>
            Approved to order{approvedAmount != null ? ` — $${approvedAmount.toFixed(2)}` : ''}
          </div>
          <div style={{ fontSize: 10, color: '#2E7D32', marginTop: 2 }}>
            Cleared by {req.approver?.full_name || 'an approver'}{req.approved_at ? ` · ${fmtDate(req.approved_at)}` : ''}
            {approvalThreshold != null ? ` · threshold $${approvalThreshold.toFixed(2)}` : ''}
          </div>
        </div>
      )}

      {isMobile && vendorSnippet && (
        <div style={{ fontSize: 11, color: '#5C5C5C', marginTop: 2 }}>{vendorSnippet}</div>
      )}
      {isMobile && !vendorSnippet && !item?.is_off_catalog && (
        <div style={{ fontSize: 11, color: '#9A9A9A', fontStyle: 'italic', marginTop: 2 }}>No vendor on file</div>
      )}
      {isMobile && item?.is_off_catalog && item?.off_catalog_vendor_name && (
        <div style={{ fontSize: 11, color: '#5C5C5C', marginTop: 2 }}>Suggested: {item.off_catalog_vendor_name}</div>
      )}

      {isOrdered && track.total > 0 && (
        <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ background: track.tracked === track.total ? '#E1F5EE' : '#E6F1FB', color: track.tracked === track.total ? '#085041' : '#0C447C', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <IconTruck size={11} /> {trackingLabel(track)}
          </span>
        </div>
      )}

      {isMobile && isOrdered && canAction && (
        <div style={{ marginTop: 6 }}>
          <button
            onClick={e => {
              e.stopPropagation();
              if (items.length === 1) openTracking(req, items[0]);
              else setMobileDetailReq(req);
            }}
            style={{
              padding: '6px 12px',
              background: track.tracked > 0 ? 'white' : '#0F0F0F',
              color: track.tracked > 0 ? '#0F0F0F' : 'white',
              border: track.tracked > 0 ? '1px solid #E0E0E0' : 'none',
              borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}
          >
            <IconTruck size={12} />
            {items.length === 1
              ? (track.tracked > 0 ? 'Update tracking' : 'Add tracking')
              : `Tracking · ${track.tracked}/${track.total}`}
          </button>
        </div>
      )}

      {isOrdered && canAction && onSaveVendorOrderRef && (
        <div style={{ marginTop: 6 }} onClick={e => e.stopPropagation()}>
          {!voEditing ? (
            <button
              onClick={e => { e.stopPropagation(); setVoEditing(true); }}
              style={{
                padding: '6px 10px', background: 'white', color: '#5C5C5C',
                border: '1px solid #E0E0E0', borderRadius: 7, fontSize: 11,
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              {req.vendor_order_ref ? `Vendor # ${req.vendor_order_ref}` : "Add vendor's order #"}
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                value={voValue}
                autoFocus
                onChange={e => setVoValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleVoSave(); } if (e.key === 'Escape') { setVoEditing(false); setVoValue(req.vendor_order_ref || ''); } }}
                placeholder="Vendor's order #"
                style={{
                  flex: '1 1 120px', minWidth: 0, padding: '6px 8px',
                  border: '1px solid #D8D8D8', borderRadius: 6,
                  fontSize: 16, // locked rule 12 -- never below 16px, iOS auto-zoom
                }}
              />
              <button
                onClick={handleVoSave}
                disabled={voSaving}
                style={{ padding: '6px 12px', background: '#0F0F0F', color: 'white', border: 'none', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
              >
                {voSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => { setVoEditing(false); setVoValue(req.vendor_order_ref || ''); setVoError(''); }}
                style={{ padding: '6px 10px', background: 'white', color: '#5C5C5C', border: '1px solid #E0E0E0', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          )}
          {voError && <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 4 }}>{voError}</div>}
        </div>
      )}

      {isMobile && isPendingApproval && canApprovePurchase && (
        <div style={{ marginTop: 6 }}>
          <button
            onClick={e => { e.stopPropagation(); openApproval(req); }}
            style={{
              padding: '6px 12px',
              background: '#993556',
              color: 'white',
              border: 'none',
              borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}
          >
            Review approval
          </button>
        </div>
      )}
    </div>
  );
}
